// Intrastat: page text and the list of countries. Generated file.
const config = {
  "slug": "intrastat",
  "name": "Intrastat",
  "menu": {
    "show": false,
    "label": "Intrastat",
    "desc": "Reports of goods moving between EU countries"
  },
  "meta": {
    "title": "Intrastat Reporting From Your ERP, by Country | Taxera",
    "description": "Intrastat explained: who must report goods moving between EU countries, how thresholds work, and how Taxera builds the declaration from your ERP data."
  },
  "hero": {
    "h1": "Intrastat declarations, built from your own ERP data.",
    "h1Accent": "Matched to your VAT reporting.",
    "text": "Intrastat records the goods you move between EU countries for trade statistics. Taxera builds the declaration from your ERP movements and keeps it consistent with your VAT return and EC Sales List.",
    "secondaryCta": "See requirements by country"
  },
  "countriesSection": {
    "heading": "Intrastat requirements by country",
    "intro": "Thresholds, deadlines and detail levels are set by each country and change every year. Pick yours to see who has to report, the key dates and how the process works. Always check the official figures.",
    "trackerPath": "/tracker/vat_returns"
  },
  "what": {
    "heading": "What is Intrastat, in plain words?",
    "paragraphs": [
      "Intrastat is a monthly statistical report of goods that move between EU countries. It is not a tax. Governments use it to measure trade inside the EU, because there are no customs declarations at the borders between member states.",
      "Only businesses above a yearly threshold have to report, and the threshold is set by each country, separately for goods received (arrivals) and goods sent (dispatches). Above a higher level, more detail is required. Deadlines, formats and the national portal differ by country."
    ],
    "explainer": {
      "heading": "Three things Intrastat depends on",
      "items": [
        {
          "icon": "Database",
          "title": "The right goods data",
          "text": "Each line needs a commodity code, value, weight, the partner country and the type of transaction. Missing or wrong codes are the most common reason for rejected declarations."
        },
        {
          "icon": "Scale",
          "title": "The right threshold",
          "text": "Whether you must report, and how much detail, depends on your country's yearly threshold. Thresholds change, and you can cross them during the year."
        },
        {
          "icon": "Clock",
          "title": "The right time",
          "text": "Declarations are monthly, with a deadline that depends on the country. A late or missing declaration can bring a penalty."
        }
      ]
    }
  },
  "steps": {
    "heading": "How Taxera does it",
    "intro": "The same ERP integration that prepares your VAT returns prepares the Intrastat declaration.",
    "items": [
      {
        "icon": "PlugZap",
        "title": "Connect to your ERP",
        "text": "Taxera sits inside your ERP landscape. No file exports and no manual uploads."
      },
      {
        "icon": "Calculator",
        "title": "Build the declaration",
        "text": "Goods movements are picked up from your transactions, with commodity codes, values and weights, using each country's layout."
      },
      {
        "icon": "Scale",
        "title": "Check and reconcile",
        "text": "The declaration is matched to your VAT reporting and flagged where figures do not agree."
      },
      {
        "icon": "CheckCircle2",
        "title": "Review and approve",
        "text": "Your team reviews the figures with drill-down to the source movements. The declaration is then ready for the route your country uses."
      }
    ]
  },
  "benefits": {
    "heading": "What changes for your team",
    "items": [
      {
        "icon": "FileSpreadsheet",
        "title": "No spreadsheets in the middle",
        "text": "The declaration comes from your ERP without anyone re-keying or patching numbers."
      },
      {
        "icon": "Layers",
        "title": "One integration for everything",
        "text": "Intrastat uses the same data as your VAT returns, EC Sales List, e-Invoicing and SAF-T."
      },
      {
        "icon": "Search",
        "title": "Codes checked before you file",
        "text": "Missing or inconsistent goods data is flagged in the platform, before the authority sees it."
      },
      {
        "icon": "BellRing",
        "title": "Rule changes are our problem",
        "text": "When a country changes a threshold, a format or a deadline, we track it and update the platform."
      }
    ]
  },
  "faq": {
    "heading": "Intrastat, questions answered",
    "items": [
      {
        "q": "What is Intrastat?",
        "a": "Intrastat is a monthly statistical report of goods that move between EU countries. It is used for trade statistics and is not a tax."
      },
      {
        "q": "Who has to file Intrastat?",
        "a": "Businesses whose yearly trade in goods with other EU countries is above their country's threshold. The thresholds are set by each country, separately for arrivals and dispatches, and they change, so check the official figures."
      },
      {
        "q": "What is the difference between arrivals and dispatches?",
        "a": "Arrivals are goods received from other EU countries. Dispatches are goods sent to other EU countries. Many countries have a different threshold for each."
      },
      {
        "q": "How does Intrastat relate to the EC Sales List and the VAT return?",
        "a": "They report different parts of the same cross-border activity. Intrastat reports the movement of goods, the EC Sales List reports who you supplied, and the VAT return reports the tax. The figures should agree."
      },
      {
        "q": "How does Taxera prepare Intrastat declarations?",
        "a": "Taxera's middleware connects to your ERP, picks up the goods movements with commodity codes, values and weights, checks them against your VAT reporting and gives your team the declaration to review and approve."
      },
      {
        "q": "Do we need a separate tool for every country?",
        "a": "No. Taxera is one platform with one ERP integration. Country rules and layouts are handled inside the platform, alongside your VAT returns."
      }
    ]
  },
  "related": [
    {
      "to": "/solutions/vat-returns",
      "label": "VAT Returns",
      "note": "The return the figures must agree with"
    },
    {
      "to": "/solutions/ec-sales-list",
      "label": "EC Sales List",
      "note": "Cross-border sales, same data"
    },
    {
      "to": "/solutions/vat-groups",
      "label": "VAT Groups",
      "note": "One return for several entities"
    },
    {
      "to": "/tracker/vat_returns",
      "label": "Live VAT reporting changes",
      "note": "Free Global Tax Tracker"
    }
  ],
  "cta": {
    "heading": "See Intrastat built from your own ERP data",
    "text": "Book a short demo. We will show you the path from your goods movements to a declaration that is ready to go."
  },
  "country": {
    "eyebrow": "Intrastat {name}",
    "headingShort": "Intrastat in {name}: {short} explained",
    "headingPlain": "Intrastat in {name}, explained",
    "mandateLabel": "The obligation",
    "whoLabel": "Who must report",
    "howLabel": "How it works",
    "datesHeading": "Key dates for {name}",
    "leftHeading": "What the report contains",
    "rightHeading": "What your ERP has to deliver",
    "flowHeading": "The process, step by step",
    "linkedHeading": "Linked obligations in {name}",
    "faqHeading": "Intrastat {name}: questions, answered",
    "moreHeading": "More Intrastat guides",
    "allLabel": "All Intrastat countries",
    "otherLabel": "Intrastat {name}",
    "downloadTitle": "Keep this guide",
    "downloadText": "Download the {name} Intrastat guide as a PDF. It has the same content as this page.",
    "printTitle": "Intrastat guide: {name}",
    "trackerPath": "/tracker/vat_returns",
    "faqs": [
      {
        "q": "What is the Intrastat obligation in {name}?",
        "a": [
          "mandateName",
          "summary"
        ]
      },
      {
        "q": "Who has to report in {name}?",
        "a": [
          "inScope"
        ]
      },
      {
        "q": "What are the key dates in {name}?",
        "a": [
          "keyDates"
        ]
      },
      {
        "q": "How does reporting work in {name}?",
        "a": [
          "process"
        ]
      },
      {
        "q": "What does the report contain in {name}?",
        "a": [
          "businessProcess"
        ]
      },
      {
        "q": "Where can I find the official rules in {name}?",
        "a": [
          "officialSources"
        ]
      }
    ]
  },
  "countries": [
    {
      "slug": "germany",
      "code": "DE",
      "name": "Germany",
      "shortName": "Intrahandelsstatistik",
      "region": "Europe",
      "status": "mandatory",
      "statusLabel": "Required above thresholds",
      "reviewed": "October 2026",
      "title": "Intrastat in Germany: Rules Explained | Taxera",
      "description": "How Intrastat works in Germany: who reports, thresholds and dates, how the declaration is prepared and what your ERP has to deliver."
    },
    {
      "slug": "france",
      "code": "FR",
      "name": "France",
      "shortName": "EMEBI",
      "region": "Europe",
      "status": "voluntary",
      "statusLabel": "Only if selected",
      "reviewed": "October 2026",
      "title": "Intrastat in France (EMEBI): Rules Explained | Taxera",
      "description": "How Intrastat works in France: who reports, thresholds and dates, how the declaration is prepared and what your ERP has to deliver."
    },
    {
      "slug": "spain",
      "code": "ES",
      "name": "Spain",
      "shortName": "Modelo Intrastat",
      "region": "Europe",
      "status": "mandatory",
      "statusLabel": "Required above threshold",
      "reviewed": "October 2026",
      "title": "Intrastat in Spain: Rules Explained | Taxera",
      "description": "How Intrastat works in Spain: who reports, thresholds and dates, how the declaration is prepared and what your ERP has to deliver."
    },
    {
      "slug": "netherlands",
      "code": "NL",
      "name": "the Netherlands",
      "shortName": "CBS",
      "region": "Europe",
      "status": "voluntary",
      "statusLabel": "Only if selected",
      "reviewed": "October 2026",
      "title": "Intrastat in the Netherlands (CBS): Rules Explained | Taxera",
      "description": "How Intrastat works in the Netherlands: who reports, thresholds and dates, how the declaration is prepared and what your ERP has to deliver."
    },
    {
      "slug": "poland",
      "code": "PL",
      "name": "Poland",
      "shortName": "PUESC",
      "region": "Europe",
      "status": "mandatory",
      "statusLabel": "Required above thresholds",
      "reviewed": "October 2026",
      "title": "Intrastat in Poland (PUESC): Rules Explained | Taxera",
      "description": "How Intrastat works in Poland: who reports, thresholds and dates, how the declaration is prepared and what your ERP has to deliver."
    }
  ]
};
export default config;
